import React, { useEffect, useState } from 'react';
import '../assets/css/cart.css';
import Footer from '../components/Footer';
import { RiShoppingBag4Fill } from "react-icons/ri";
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { uploadPrescription } from '../Api/media';
import { getLocationPincode, getPincodeByNumber } from '../Api/location';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import * as mammoth from 'mammoth';
import { getGSTByCartAndPincode } from '../Api/gst';
import { cartRequiresPrescription } from '../Api/prescriptions';
import { MdDelete } from "react-icons/md";

const Cart = () => {
    const navigate = useNavigate();

    // Load cart items
    const [cartItems, setCartItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('cart')) || [];
        } catch (err) {
            return [];
        }
    });

    const [isAuthenticated] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('isAuthenticated')) || false;
        } catch {
            return false;
        }
    });

    const [pincode, setPincode] = useState(() => localStorage.getItem('pincode') || '');
    const [validPincodes, setValidPincodes] = useState([]);
    // Did the serviceable-pincode list actually load? Distinct from "is empty",
    // which is what the old code could not tell apart. See BUG-11 below.
    const [pincodesLoaded, setPincodesLoaded] = useState(false);
    // Set while the prescription-requirement lookup is in flight, so the
    // checkout button cannot be pressed twice during it.
    const [checkingPrescription, setCheckingPrescription] = useState(false);
    const [isPincodeValid, setIsPincodeValid] = useState(null);
    const [isCheckingPincode, setIsCheckingPincode] = useState(false);

    const [prescriptionFile, setPrescriptionFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [prescriptionId, setPrescriptionId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [needsPrescriptionUpload, setNeedsPrescriptionUpload] = useState(false);

    const [subtotal, setSubtotal] = useState(0);
    const [gst, setGst] = useState(0);
    const [gstBreakup, setGstBreakup] = useState({ cgst: 0, sgst: 0 });

    //  Dynamic shipping charges (replaces static 5.0)
    const [shipping, setShipping] = useState(0);



    // Keep cart synced to local storage
    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));

        const newSubtotal = cartItems.reduce((sum, item) => {
            const price = getItemPrice(item);
            const qty = Number(item.quantity || 1);
            return sum + price * qty;
        }, 0);

        setSubtotal(newSubtotal);
    }, [cartItems]);

    // Redirect superadmin
    useEffect(() => {
        try {
            const userData = JSON.parse(localStorage.getItem('user_data')) || {};
            if (userData.isSuperAdmin) navigate('/dashboard');
        } catch { }
    }, [navigate]);

    // Fetch valid pincodes for validation.
    //
    // BUG-11: this list arrives asynchronously, but the check below treated an
    // empty list as "not serviceable". So submitting a pincode before the fetch
    // resolved — or at any point after it failed — told the customer "Delivery
    // not available" for an address we deliver to, and blocked checkout. The
    // load state is now tracked so the check can say "still loading" and
    // "couldn't check" instead of confidently giving the wrong answer.
    useEffect(() => {
        async function fetchPincodes() {
            setIsCheckingPincode(true);
            try {
                const response = await getLocationPincode();
                const pins = (response || []).map((it) =>
                    typeof it === 'string' ? it : it.pincode || it.code
                );
                setValidPincodes(pins);
                setPincodesLoaded(true);
            } catch (error) {
                setPincodesLoaded(false);
                toast.error('Error fetching PIN codes.');
            } finally {
                setIsCheckingPincode(false);
            }
        }
        fetchPincodes();
    }, []);

    // 🔥 Fetch shipping charge dynamically (Important Part)
    useEffect(() => {
        async function fetchShipping() {
            const pin = localStorage.getItem('pincode') || pincode;

            if (!pin) {
                setShipping(0);
                return;
            }

            try {
                const data = await getPincodeByNumber(pin);
                if (data?.charge) {
                    setShipping(Number(data.charge));
                } else {
                    setShipping(0);
                }
            } catch (err) {
                console.error("Shipping fetch error:", err);
                setShipping(0);
            }
        }

        fetchShipping();
    }, [pincode]);

    /*useEffect(() => {
      if (!cartItems.length || !pincode) {
        setGst(0);
        return;
      }
    
      let cancelled = false;
    
      const fetchGST = async () => {
        try {
          const data = await getGSTByCartAndPincode({
            cartItems,
            pincode
          });
    
          if (!cancelled) {
            setGst(Number(data?.tax?.total ?? 0));
          }
        } catch {
          if (!cancelled) setGst(0);
        }
      };
    
      fetchGST();
    
      return () => {
        cancelled = true;
      };
    }, [cartItems, pincode]); //  FIXED SIZE, FIXED ORDER
    */
    const gstRequestRef = React.useRef(0);

    useEffect(() => {
        if (!cartItems.length || !pincode) {
            setGst(0);
            return;
        }

        const currentRequest = ++gstRequestRef.current;

        const timer = setTimeout(async () => {
            try {
                const data = await getGSTByCartAndPincode({
                    cartItems,
                    pincode
                });

                //  Only update if this is the latest request
                if (currentRequest === gstRequestRef.current) {
                    const totalTax = Number(data?.tax?.total ?? 0);
                    const cgst = Number(data?.tax?.breakup?.CGST ?? 0);
                    const sgst = Number(data?.tax?.breakup?.SGST ?? 0);

                    setGst(totalTax);
                    setGstBreakup({ cgst, sgst });
                }
            } catch {
                if (currentRequest === gstRequestRef.current) {
                    setGst(0);
                }
            }
        }, 400); // ⏱ debounce

        return () => clearTimeout(timer);

    }, [cartItems, pincode]);




    // Helpers
    function getItemPrice(item) {
        return item.sale_price && !isNaN(parseFloat(item.sale_price))
            ? parseFloat(item.sale_price)
            : item.regular_price && !isNaN(parseFloat(item.regular_price))
                ? parseFloat(item.regular_price)
                : item.price
                    ? parseFloat(item.price)
                    : 0;
    }

    const increment = (index) => {
        setCartItems(prev =>
            prev.map((item, i) =>
                i === index
                    ? { ...item, quantity: (item.quantity || 1) + 1 }
                    : item
            )
        );
    };

    const decrement = (index) => {
        setCartItems(prev =>
            prev.map((item, i) =>
                i === index && (item.quantity || 1) > 1
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            )
        );
    };


    const removeItem = (index) => {
        setCartItems(prev => prev.filter((_, i) => i !== index));
    };

    // File conversion helpers
    const convertImageToPDF = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    try {
                        const doc = new jsPDF();
                        const pageWidth = doc.internal.pageSize.getWidth();
                        const pageHeight = doc.internal.pageSize.getHeight();

                        const ratio = Math.min(pageWidth / img.width, pageHeight / img.height);
                        const width = img.width * ratio;
                        const height = img.height * ratio;

                        doc.addImage(reader.result, 'JPEG', 0, 0, width, height);
                        const pdfBlob = doc.output('blob');
                        resolve(new File([pdfBlob], `${file.name}.pdf`, { type: 'application/pdf' }));
                    } catch (err) {
                        reject(err);
                    }
                };
            };
            reader.readAsDataURL(file);
        });
    };

    const convertDocxToPDF = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const { value: html } = await mammoth.convertToHtml({ arrayBuffer });

        const doc = new jsPDF();
        await new Promise((resolve) => {
            doc.html(html, {
                callback: () => resolve(),
                x: 10,
                y: 10,
            });
        });
        const pdfBlob = doc.output('blob');
        return new File([pdfBlob], `${file.name}.pdf`, { type: 'application/pdf' });
    };

    // File upload handling
    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return toast.error("No file selected");

        try {
            let processedFile = file;

            if (file.type.includes("image")) {
                processedFile = await convertImageToPDF(file);
            } else if (file.type.includes("word")) {
                processedFile = await convertDocxToPDF(file);
            }

            setPrescriptionFile(processedFile);
            setUploadStatus("File ready for upload");
            toast.success("File processed");
        } catch (error) {
            setUploadStatus("Error processing file");
            toast.error("Error processing file");
        }
    };

    // Upload prescription
    const handleUpload = async () => {
        if (!prescriptionFile) return toast.error("Select a file first");

        try {
            setIsUploading(true);
            setUploadStatus("Uploading...");

            const userId = JSON.parse(localStorage.getItem('user_data'))?.id;
            const response = await uploadPrescription(prescriptionFile, userId);

            const id = response?.id || null;
            setPrescriptionId(id);

            toast.success("Uploaded successfully");
            navigate('/checkout', { state: { customer_note: id } });
        } catch (error) {
            toast.error("Upload failed");
        } finally {
            setIsUploading(false);
        }
    };

    // Pincode validation
    const handlePincodeSubmit = (e) => {
        e.preventDefault();

        const trimmed = pincode.trim();
        if (!trimmed || trimmed.length !== 6) {
            toast.error("Enter valid pincode");
            return;
        }

        // Still loading, or the list never arrived: say so rather than reporting
        // a serviceable pincode as undeliverable.
        if (!pincodesLoaded) {
            toast.error(
                isCheckingPincode
                    ? "Still loading delivery areas — try again in a moment."
                    : "Couldn't load delivery areas. Please refresh and try again."
            );
            return;
        }

        const valid = validPincodes.includes(trimmed);
        setIsPincodeValid(valid);

        if (valid) {
            localStorage.setItem("pincode", trimmed);
            toast.success("Pincode valid");
        } else {
            toast.error("Delivery not available");
        }
    };

    // Checkout flow
    const productsCheckout = async () => {
        // Same BUG-11 trap: an unloaded list must not read as "invalid pincode",
        // or checkout is blocked with a message that sends the customer to fix
        // something that was never wrong.
        if (!pincodesLoaded) {
            toast.error(
                isCheckingPincode
                    ? "Still loading delivery areas — try again in a moment."
                    : "Couldn't load delivery areas. Please refresh and try again."
            );
            return;
        }

        if (!pincode || !validPincodes.includes(pincode)) {
            toast.error("Validate your pincode first");
            return;
        }

        // Ask the server which of these items need a prescription, rather than
        // trusting the `purchase_note` copy stored alongside the cart in
        // localStorage. That copy is taken when the item is added, so an item
        // added before a product was marked prescription-only kept the stale
        // value and silently skipped the gate. See Api/prescriptions.js.
        //
        // `cartRequiresPrescription` fails closed: a failed lookup reports that a
        // prescription IS needed, because being wrong in the permissive
        // direction means dispensing prescription-only medicine without one.
        setCheckingPrescription(true);
        let requiresPrescription;
        let degraded;
        try {
            const result = await cartRequiresPrescription(cartItems);
            requiresPrescription = result.required;
            degraded = result.degraded;
        } finally {
            setCheckingPrescription(false);
        }

        if (requiresPrescription && !prescriptionFile) {
            setNeedsPrescriptionUpload(true);
            toast.error(
                degraded
                    ? "We couldn't confirm whether these items need a prescription, so please upload one to continue."
                    : "Upload prescription first"
            );
            return;
        }

        if (requiresPrescription) {
            handleUpload();
        } else {
            navigate('/checkout');
        }
    };

    const itemsCount = cartItems.reduce((sum, it) => sum + (it.quantity || 1), 0);
    const tax = gst;
    const grandTotal = subtotal + shipping + tax;

    return (
        <>
            <div className="min-h-screen">
                <main className="container main-content">
                    <div className="cart-layout">

                        {/* CART ITEMS */}
                        <div className="cart-list-section">
                            <div className="cart-header">
                                <h1 className="cart-title">Your Cart</h1>
                                <span className="item-count">{itemsCount} items</span>
                            </div>

                            <div className="cart-items-wrapper">
                                {cartItems.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                                        <RiShoppingBag4Fill style={{ fontSize: '50px' }} />
                                        <p>Your cart is empty</p>
                                    </div>
                                ) : (
                                    cartItems.map((item, index) => {
                                        const image = item?.images?.[0]?.src || item.image;
                                        const name = item?.name || item?.title || "Product";
                                        const category = item?.categories?.[0]?.name || "General";

                                        const price = getItemPrice(item);
                                        const qty = item.quantity || 1;
                                        const lineTotal = price * qty;

                                        return (
                                            <div key={index} className="cart-item-card">
                                                <img src={image} className="item-image" alt="" />

                                                <div className="item-details">
                                                    <h3 className="item-name">{name}</h3>
                                                    <p className="item-category">{category}</p>
                                                    <p className="item-price">₹{price.toFixed(2)} / each</p>
                                                    <p className="item-price"><b>Item total: ₹{lineTotal.toFixed(2)}</b></p>
                                                </div>

                                                <div className="item-actions-mobile-row">
                                                    <div className="quantity-controls">
                                                        <button className="qty-btn" onClick={() => decrement(index)}>-</button>
                                                        <span className="qty-display">{qty}</span>
                                                        <button className="qty-btn" onClick={() => increment(index)}>+</button>
                                                    </div>

                                                    <button className="delete-btn" onClick={() => removeItem(index)}>
                                                        <span className="material-icons-outlined"><MdDelete style={{marginTop:'3px'}} /></span> Remove
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* SUMMARY SECTION */}
                        <div className="summary-section">
                            {/* PINCODE + PRESCRIPTION */}
                            <div className="summary-card" style={{ marginBottom: '1.5rem' }}>
                                <h2 style={{ color: '#10b981' }}>Delivery & Prescription</h2>

                                <form onSubmit={handlePincodeSubmit} className="pincode-form">
                                    <label>Check Delivery Pincode</label>
                                    <div className="flex space-x-2">
                                        <input
                                            value={pincode}
                                            onChange={(e) => setPincode(e.target.value)}
                                            maxLength="6"
                                            placeholder="Enter 6-digit pincode"
                                            className="form-input"
                                        />
                                        <button
                                            className="pincode-check-btn"
                                            disabled={isCheckingPincode}
                                        >
                                            {isCheckingPincode ? "Checking..." : "Check"}
                                        </button>
                                    </div>

                                    {isPincodeValid !== null && (
                                        <p className="pincode-message">
                                            {isPincodeValid ? "Delivery available" : "Delivery not available"}
                                        </p>
                                    )}
                                </form>

                                {/* Prescription Upload */}
                                <div className="prescription-upload-section">
                                    <label>Upload Prescription (Optional)</label>
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.pdf,.docx"
                                        onChange={handleFileChange}
                                        className="file-input"
                                    />

                                    {uploadStatus && (
                                        <p className="file-name-display" style={{ color: "green" }}>
                                            {uploadStatus}
                                        </p>
                                    )}

                                    {needsPrescriptionUpload && !prescriptionFile && (
                                        <p style={{ color: 'orange' }}>
                                            This order requires a prescription. Upload to continue.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* ORDER SUMMARY */}
                            <div className="summary-card">
                                <h2 className="summary-title">Order Summary</h2>

                                <div className="summary-row">
                                    <span>Subtotal</span>
                                    <span>₹{subtotal.toFixed(2)}</span>
                                </div>

                                <div className="summary-row">
                                    <span>Shipping</span>
                                    <span>₹{shipping.toFixed(2)}</span>
                                </div>

                                <div className="summary-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                                    <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                        <span>GST</span>
                                        <span>₹{tax.toFixed(2)}</span>
                                    </div>

                                    {(gstBreakup.cgst || gstBreakup.sgst) && (
                                        <small style={{ color: "#525459ff", marginTop: "4px" }}>
                                            (cgst  ₹{gstBreakup.cgst.toFixed(2)} + sgst ₹{gstBreakup.sgst.toFixed(2)})
                                        </small>

                                    )}
                                </div>

                                <div className="summary-divider">
                                    <div className="total-row">
                                        <span>Total</span>
                                        <span>₹{grandTotal.toFixed(2)}</span>
                                    </div>
                                </div>

                                <button
                                    className="checkout-btn"
                                    onClick={productsCheckout}
                                    disabled={checkingPrescription}
                                >
                                    {checkingPrescription ? 'Checking…' : 'Proceed to Checkout'}
                                </button>
                            </div>
                        </div>

                    </div>
                </main>
            </div>
        </>
    );
};

export default Cart;
